import { currentTimestamp, readableTimeSince } from '@/utils/time'
import {
  AGT_DEBUG,
  MAP_STATUSES,
  MINUTES_BEFORE_OUT_OF_ROOM,
  MINUTES_BEFORE_VIBE_OFFLINE,
  URGENT_EVENT_SEVERITY,
} from '@/utils/constants'
import { getFriendlyEventName } from '@/view-model/patient-event-constants/MemberPatientEvents'
import { ONE_HOUR_TIMESTAMP } from '@/components/patient-stats/graphs/mapping-chart-options/constants'
import { isEventAddressed } from '@/jsonapi/PatientWithEvents'

interface PersonData {
  id: string
  firstName?: string
  lastName?: string

  [propName: string]: any
}

export function getPatientName(patient: PersonData): string {
  if (patient.firstName || patient.lastName) {
    return [patient.firstName, patient.lastName].filter(Boolean).join(' ')
  } else {
    return patient.id
  }
}

export function getPatientInitials(patient: PersonData): string {
  if (patient.firstName || patient.lastName) {
    return [patient.firstName, patient.lastName]
      .filter(Boolean)
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
  } else {
    return patient.id.substring(0, 2)
  }
}

export function sortByPatientName(patients: PersonData[]) {
  return patients.sort((a, b) =>
    getPatientName(a)
      .toLowerCase()
      .localeCompare(getPatientName(b).toLowerCase())
  )
}

interface HasUserData {
  userFirstName: string
  userLastName: string
}

export interface MapCalloutStatus {
  label: string
  type: string
}

export function getUserName(hasUserData: HasUserData): string {
  return getPatientName({
    firstName: hasUserData.userFirstName,
    lastName: hasUserData.userLastName,
    id: '',
  })
}

export function getCareGiverName(caregiver: PersonData): string {
  if (caregiver.firstName || caregiver.lastName) {
    return [caregiver.firstName, caregiver.lastName].filter(Boolean).join(' ')
  } else {
    return caregiver.id
  }
}

// location info

export function isVibeOnline(patient: PersonData): boolean {
  if (!patient) {
    return false
  }
  let offlineCutoff = currentTimestamp() - MINUTES_BEFORE_VIBE_OFFLINE * 60 * 1000
  if (patient.lastVibeConnectionTimestamp === null || patient.lastVibeConnectionTimestamp < offlineCutoff) {
    return false
  }
  return true
}

export function isInOwnRoom(patient: PersonData): boolean {
  if (!patient) {
    return false
  }

  // check if patient is in a shared room
  if (patient.lastConnectionSharedRoomId) {
    return false
  }

  return isInAnyRoom(patient)
}

export function isInAnyRoom(patient: PersonData): boolean {
  if (!patient) {
    return false
  }

  return patient.lastTrackerConnectionTimestamp > currentTimestamp() - MINUTES_BEFORE_OUT_OF_ROOM * 60 * 1000
}

export function getInRoomLabel(patient): string {
  if (!patient) {
    return null
  }
  if (patient.lastTrackerConnectionTimestamp === null) {
    return 'No tracker'
  }

  if (!isVibeOnline(patient)) {
    return 'Vibe offline'
  }

  if (!isInAnyRoom(patient)) {
    return 'Unknown'
  }

  if (patient.lastLocationName) {
    return 'In ' + patient.lastLocationName
  } else {
    return 'In room' // own room
  }
}

// for pop-up (last online 2 hours ago etc)
export function getLastInRoomLabel(patient, showTimeIfNotInRoom): string {
  if (!patient) {
    return null
  }
  if (patient.lastTrackerConnectionTimestamp === null) {
    return 'No tracker'
  }

  if (!isVibeOnline(patient)) {
    if (patient.lastVibeConnectionTimestamp) {
      return 'Vibe last connected ' + readableTimeSince({ timestamp: patient.lastVibeConnectionTimestamp })
    } else {
      return null
    }
  }

  if (!isInAnyRoom(patient)) {
    return 'Out of room'
  }

  // todo, determine by shared-room-id once all vibes are linked
  if (patient.lastLocationName) {
    return 'In ' + patient.lastLocationName
  } else {
    return 'In room' // own room
  }
}

export function getMapCalloutStatus(patient): MapCalloutStatus {
  if (!patient) {
    return {
      label: 'No Resident',
      type: null,
    }
  }

  if (patient.patientEvent === null) {
    return {
      label: 'On Track',
      type: MAP_STATUSES.Good,
    }
  }

  if (patient.patientEvent.eventSeverity < URGENT_EVENT_SEVERITY) {
    return {
      label: getFriendlyEventName(patient.patientEvent.eventName),
      type: MAP_STATUSES.Attention,
    }
  } else {
    return {
      // shouldn't really be any critical, since no non-live critical events apply to NH
      label: getFriendlyEventName(patient.patientEvent.eventName),
      type: MAP_STATUSES.Critical,
    }
  }
}

export function getMapLocationEventColor(patient): String {
  // old method - used to include live and consolidated events.
  // now, we want to just include live events, and only if unaddressed (like the map)

  // let status = getMapEventStatus(patient)
  // switch (status.type) {
  //   case MAP_STATUSES.Good:
  //     return 'steelblue'
  //   default:
  //     return 'red'
  // }
  if (
    patient &&
    patient.liveEvents &&
    patient.liveEvents[0] &&
    hasUnaddressedLiveAlert(patient.id, patient.liveEvents[0])
  ) {
    return 'red'
  }
  return 'steelblue'
}

export function hasUnaddressedLiveAlert(patientId, liveEvent): boolean {
  if (!liveEvent || liveEvent.eventType !== 'live') {
    // should be redundant
    return false
  }

  // if event is older than 1 hour
  if (liveEvent.eventTimestamp < currentTimestamp() - ONE_HOUR_TIMESTAMP) {
    if (!AGT_DEBUG) {
      return false
    }
  }
  return !isEventAddressed(patientId, liveEvent)
}
